/* eslint-disable react/jsx-props-no-spreading -- wrappers here forward props wholesale */
import PropTypes from 'prop-types';
import React from 'react';

import displayName from './displayName';
import { canUseDOM } from './dom';

// Dev only: dev react rethrows to window.onerror first, and airbrake's own duplicate filter would
// drop this tagged notice as a copy. Production does not rethrow, where this is the only copy.
const NOTIFY_DELAY_MS = process.env.NODE_ENV === 'production' ? 0 : 1200;

// Snapshot at catch time, not prevProps: pcb mutates the board tree in place, so a module's
// resetKey is the same object after an edit.
const NO_SNAPSHOT = Symbol('no snapshot');
const snapshot = value => {
  try {
    return JSON.stringify(value);
  } catch (e) {
    return NO_SNAPSHOT;
  }
};

// context is errbit's Where column; unknown context keys are stored but never displayed.
export const logToErrbit = (error, component, params) => {
  if (canUseDOM() && window.notify_errbit) {
    setTimeout(() => window.notify_errbit({ error, context: { component }, params }), NOTIFY_DELAY_MS);
  } else {
    console.error('Would send this error to errbit', error, component, params);
  }
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.errorSnapshot = NO_SNAPSHOT;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidUpdate() {
    const { resetKey } = this.props;
    const { hasError } = this.state;

    if (hasError && this.errorSnapshot !== NO_SNAPSHOT && snapshot(resetKey) !== this.errorSnapshot) {
      this.errorSnapshot = NO_SNAPSHOT;
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error, errorInfo) {
    const { component, context, resetKey } = this.props;

    this.errorSnapshot = snapshot(resetKey);
    logToErrbit(error, component, { ...context, componentStack: errorInfo?.componentStack });
  }

  render() {
    const { hasError } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      return fallback;
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
  component: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  context: PropTypes.object,
  fallback: PropTypes.node,
  // eslint-disable-next-line react/forbid-prop-types
  resetKey: PropTypes.any,
};

ErrorBoundary.defaultProps = {
  children: null,
  component: null,
  context: {},
  fallback: <h2>Something went wrong.</h2>,
  resetKey: null,
};

export const withErrorBoundary = (Component, name) => {
  const component = name || displayName(Component);
  const Wrapped = props => (
    <ErrorBoundary component={component} fallback={null}>
      <Component {...props} />
    </ErrorBoundary>
  );

  Wrapped.displayName = `WithErrorBoundary(${component})`;

  return Wrapped;
};

export default ErrorBoundary;
